/**
 * BED — Révocation des liens d'un équipage (migration 00293, audit CX-6).
 *
 * Ferme le lien du bulletin ET les liens de paiement du solde : révoquer le
 * seul lien du bulletin laissait le participant payer 30 jours de plus. Motif
 * obligatoire, tracé dans la timeline. « Régénérer le lien BED » rouvre
 * ensuite le même bulletin (réponses conservées) ; la préparation des liens
 * de l'ouverture en émet un neuf si l'équipage reste dans la vague 1.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../ui/button';
import { Form, FormInput, toast } from '../ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { bedOpeningErrorMessage, revokeBedCrewLinks } from '../../lib/bed/opening';

const revokeSchema = z.object({
  reason: z.string().trim().min(3, 'Indiquez le motif (3 caractères minimum).'),
});

type RevokeFormValues = z.infer<typeof revokeSchema>;

interface BedRevokeLinksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  crewId: string;
  crewName: string;
  onRevoked: () => void;
}

export function BedRevokeLinksDialog({
  open,
  onOpenChange,
  crewId,
  crewName,
  onRevoked,
}: BedRevokeLinksDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<RevokeFormValues>({
    resolver: zodResolver(revokeSchema),
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) form.reset({ reason: '' });
  }, [open, form]);

  async function handleRevoke(values: RevokeFormValues) {
    setSubmitting(true);
    const res = await revokeBedCrewLinks(crewId, values.reason);
    setSubmitting(false);
    if (res.status !== 'ok') {
      toast.error(bedOpeningErrorMessage(res.status));
      return;
    }
    toast.success(
      `Liens révoqués : ${res.data.form_links} lien(s) du bulletin, ${res.data.payment_links} lien(s) de paiement.`
    );
    onOpenChange(false);
    onRevoked();
  }

  function handleClose(value: boolean) {
    if (submitting) return;
    onOpenChange(value);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Révoquer les liens du bulletin</DialogTitle>
          <DialogDescription>
            <strong>{crewName}</strong> — le lien du bulletin et les liens de paiement du solde
            cessent de fonctionner immédiatement. Tant que le bulletin reste modifiable, « Générer
            le lien BED » en rouvre un nouveau avec les réponses déjà saisies. La préparation des
            liens de l&apos;ouverture lui en émet aussi un nouveau s&apos;il reste dans la vague 1 :
            pour l&apos;écarter, changez son statut.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleRevoke)} className="flex flex-col gap-4">
            <FormInput
              control={form.control}
              name="reason"
              label="Motif"
              placeholder="Ex. lien transmis à la mauvaise personne"
              required
              disabled={submitting}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={submitting}
              >
                Annuler
              </Button>
              <Button type="submit" variant="destructive" disabled={submitting}>
                Révoquer
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
